/**
 * Returns user-facing labels for a given match_type.
 * Internal code still uses "match" everywhere — this is only for display text.
 */
export function getMatchTypeLabels(matchType) {
  switch (matchType) {
    case 'quick_round':
      return {
        singular: 'Single Round',
        tabLabel: 'Round',
        createLabel: 'Start Single Round',
        nameLabel: 'Round Name',
        finishLabel: 'Finish Round',
        finishConfirmTitle: 'Finish Round?',
        finishConfirmBody: 'This will mark the round as completed. You can reopen it later if needed.',
        finishConfirmButton: 'Confirm Finish Round',
        activateLabel: 'Activate Round',
        reopenLabel: 'Reopen Round',
        reopenConfirmTitle: 'Reopen Round?',
        reopenConfirmBody: 'This will set the round back to active. Scores and settlements can be edited again.',
        reopenConfirmButton: 'Confirm Reopen Round',
        deleteConfirm: 'Delete this round? This cannot be undone.',
        deleteConfirmBody: 'All scores and settlements will be permanently deleted.',
        editNameTitle: 'Edit round name',
        loadingMessage: 'Loading round...',
        notFoundMessage: 'Round not found',
        sideBetsLabel: 'Side Games',
        lowNetEntireLabel: 'Lowest Net',
        lowGrossEntireLabel: 'Lowest Gross',
        stablefordEntireLabel: 'Stableford',
        skinsEntireLabel: 'Skins',
        inThisLabel: 'In This Round',
        entireSuffix: '(round)',
        activateHint: 'Activate the round to start scoring',
        noRoundsHint: 'No rounds yet. Start one!',
      }
    case 'match':
      return {
        singular: 'Mini Match',
        tabLabel: 'Match',
        createLabel: 'Create Mini Match',
        nameLabel: 'Match Name',
        finishLabel: 'Finish Match',
        finishConfirmTitle: 'Finish Match?',
        finishConfirmBody: 'This will mark the match as completed. You can reopen it later if needed.',
        finishConfirmButton: 'Confirm Finish Match',
        activateLabel: 'Activate Match',
        reopenLabel: 'Reopen Match',
        reopenConfirmTitle: 'Reopen Match?',
        reopenConfirmBody: 'This will set the match back to active. Scores and settlements can be edited again.',
        reopenConfirmButton: 'Confirm Reopen Match',
        deleteConfirm: 'Delete this match? This cannot be undone.',
        deleteConfirmBody: 'All rounds, scores, and settlements will be permanently deleted.',
        editNameTitle: 'Edit match name',
        loadingMessage: 'Loading match...',
        notFoundMessage: 'Match not found',
        sideBetsLabel: 'Match Side Games',
        lowNetEntireLabel: 'Lowest Net — Entire Match',
        lowGrossEntireLabel: 'Lowest Gross — Entire Match',
        stablefordEntireLabel: 'Stableford — Entire Match',
        skinsEntireLabel: 'Skins — Entire Match',
        inThisLabel: 'In This Match',
        entireSuffix: '(entire match)',
        activateHint: 'Activate the match to start adding rounds',
        noRoundsHint: 'No rounds yet. Start one!',
      }
    case 'team_match':
      return {
        singular: 'Team Match',
        tabLabel: 'Match',
        createLabel: 'Create Team Match',
        nameLabel: 'Match Name',
        finishLabel: 'Finish Match',
        finishConfirmTitle: 'Finish Team Match?',
        finishConfirmBody: 'This will mark the team match as completed. You can reopen it later if needed.',
        finishConfirmButton: 'Confirm Finish Match',
        activateLabel: 'Continue to Team Setup',
        reopenLabel: 'Reopen Match',
        reopenConfirmTitle: 'Reopen Team Match?',
        reopenConfirmBody: 'This will set the team match back to active. Scores and settlements can be edited again.',
        reopenConfirmButton: 'Confirm Reopen Match',
        deleteConfirm: 'Delete this team match? This cannot be undone.',
        deleteConfirmBody: 'All rounds, scores, and settlements will be permanently deleted.',
        editNameTitle: 'Edit match name',
        loadingMessage: 'Loading team match...',
        notFoundMessage: 'Team match not found',
        sideBetsLabel: 'Match Side Games',
        lowNetEntireLabel: 'Lowest Net — Entire Match',
        lowGrossEntireLabel: 'Lowest Gross — Entire Match',
        stablefordEntireLabel: 'Stableford — Entire Match',
        skinsEntireLabel: 'Skins — Entire Match',
        inThisLabel: 'In This Match',
        entireSuffix: '(entire match)',
        activateHint: 'Add players to get started',
        noRoundsHint: 'No sessions yet. Set up a session to get started!',
      }
    case 'invitational':
      return {
        singular: 'Invitational',
        tabLabel: 'Event',
        createLabel: 'Create Invitational',
        nameLabel: 'Event Name',
        finishLabel: 'Finish Invitational',
        finishConfirmTitle: 'Finish Invitational?',
        finishConfirmBody: 'This will mark the invitational as completed. You can reopen it later if needed.',
        finishConfirmButton: 'Confirm Finish Invitational',
        activateLabel: 'Activate Invitational',
        reopenLabel: 'Reopen Invitational',
        reopenConfirmTitle: 'Reopen Invitational?',
        reopenConfirmBody: 'This will set the invitational back to active. Scores and settlements can be edited again.',
        reopenConfirmButton: 'Confirm Reopen Invitational',
        deleteConfirm: 'Delete this invitational? This cannot be undone.',
        deleteConfirmBody: 'All rounds, scores, and settlements will be permanently deleted.',
        editNameTitle: 'Edit event name',
        loadingMessage: 'Loading invitational...',
        notFoundMessage: 'Invitational not found',
        sideBetsLabel: 'Event Side Games',
        lowNetEntireLabel: 'Lowest Net — Entire Event',
        lowGrossEntireLabel: 'Lowest Gross — Entire Event',
        stablefordEntireLabel: 'Stableford — Entire Event',
        skinsEntireLabel: 'Skins — Entire Event',
        inThisLabel: 'In This Event',
        entireSuffix: '(entire event)',
        activateHint: 'Add players and activate to start setting up teams and sessions',
        noRoundsHint: 'No sessions yet. Create one to get started!',
      }
    case 'trip':
    default:
      return {
        singular: 'Golf Trip',
        tabLabel: 'Trip',
        createLabel: 'Create Trip',
        nameLabel: 'Trip Name',
        finishLabel: 'Finish Trip',
        finishConfirmTitle: 'Finish Trip?',
        finishConfirmBody: 'This will mark the trip as completed. You can reopen it later if needed.',
        finishConfirmButton: 'Confirm Finish Trip',
        activateLabel: 'Activate Trip',
        reopenLabel: 'Reopen Trip',
        reopenConfirmTitle: 'Reopen Trip?',
        reopenConfirmBody: 'This will set the trip back to active. Scores and settlements can be edited again.',
        reopenConfirmButton: 'Confirm Reopen Trip',
        deleteConfirm: 'Delete this trip? This cannot be undone.',
        deleteConfirmBody: 'All rounds, scores, and settlements will be permanently deleted.',
        editNameTitle: 'Edit trip name',
        loadingMessage: 'Loading trip...',
        notFoundMessage: 'Trip not found',
        sideBetsLabel: 'Trip Side Games',
        lowNetEntireLabel: 'Lowest Net — Entire Trip',
        lowGrossEntireLabel: 'Lowest Gross — Entire Trip',
        stablefordEntireLabel: 'Stableford — Entire Trip',
        skinsEntireLabel: 'Skins — Entire Trip',
        inThisLabel: 'In This Trip',
        entireSuffix: '(entire trip)',
        activateHint: 'Activate the trip to start adding rounds',
        noRoundsHint: 'No rounds yet. Start one!',
      }
  }
}
